import { supabase } from '../supabaseClient';

export interface TutorialData {
  id: number;
  trainer_id: string;
  has_completed_tutorial: boolean;
  tutorial_completed_at: string | null;
  tutorial_skipped_at: string | null;
  current_step: number;
  total_steps: number;
  created_at: string;
  updated_at: string;
}

export class TutorialService {
  /**
   * Get or create tutorial record for the current trainer
   */
  static async getTutorialData(): Promise<TutorialData | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .rpc('get_or_create_tutorial_record', { trainer_uuid: user.id });

      if (error) {
        console.error('Error getting tutorial data:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getTutorialData:', error);
      return null;
    }
  }

  /**
   * Check if trainer needs to see the tutorial
   */
  static async shouldShowTutorial(): Promise<boolean> {
    try {
      const tutorialData = await this.getTutorialData();
      if (!tutorialData) return false;

      // Show tutorial if not completed and not skipped
      return !tutorialData.has_completed_tutorial && !tutorialData.tutorial_skipped_at;
    } catch (error) {
      console.error('Error checking tutorial status:', error);
      return false;
    }
  }

  /**
   * Mark tutorial as completed
   */
  static async completeTutorial(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .rpc('complete_tutorial', { trainer_uuid: user.id });

      if (error) {
        console.error('Error completing tutorial:', error);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error in completeTutorial:', error);
      return false;
    }
  }

  /**
   * Skip tutorial
   */
  static async skipTutorial(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .rpc('skip_tutorial', { trainer_uuid: user.id });

      if (error) {
        console.error('Error skipping tutorial:', error);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error in skipTutorial:', error);
      return false;
    }
  }

  /**
   * Update tutorial progress
   */
  static async updateProgress(step: number): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .rpc('update_tutorial_progress', { 
          trainer_uuid: user.id, 
          step_number: step 
        });

      if (error) {
        console.error('Error updating tutorial progress:', error);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error in updateProgress:', error);
      return false;
    }
  }

  /**
   * Reset tutorial for testing purposes
   */
  static async resetTutorial(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('trainer_tutorials')
        .update({
          has_completed_tutorial: false,
          tutorial_completed_at: null,
          tutorial_skipped_at: null,
          current_step: 0,
          updated_at: new Date().toISOString()
        })
        .eq('trainer_id', user.id);

      if (error) {
        console.error('Error resetting tutorial:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in resetTutorial:', error);
      return false;
    }
  }
}
